import { useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../lib/api';

type Mode = 'DIRECT' | 'OPTIMIZED' | 'STRICT_BRAND';
type Length = 'SHORT' | 'STANDARD' | 'DETAILED';

export function ImageStudioPage() {
  const [brands, setBrands] = useState<{ id: string; name: string }[]>([]);
  const [brandId, setBrandId] = useState('');
  const [brief, setBrief] = useState('');
  const [subject, setSubject] = useState('');
  const [aspectRatio, setAspectRatio] = useState('9:16');
  const [mode, setMode] = useState<Mode>('OPTIMIZED');
  const [length, setLength] = useState<Length>('STANDARD');
  const [textPolicy, setTextPolicy] = useState<'NO_GENERATED_TEXT' | 'TEXT_IN_IMAGE'>('NO_GENERATED_TEXT');
  const [llm, setLlm] = useState(true);
  const [optimized, setOptimized] = useState<Awaited<ReturnType<typeof api.imagePromptOptimize>> | null>(null);
  const [editablePrompt, setEditablePrompt] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [generated, setGenerated] = useState<{ url: string; provider: string } | null>(null);

  useEffect(() => {
    api.brands.list().then((b) => {
      setBrands(b.map((x: { id: string; name: string }) => ({ id: x.id, name: x.name })));
      if (b[0]) setBrandId(b[0].id);
    }).catch(() => undefined);
  }, []);

  const modifiers = useMemo(() => [
    { label: 'Shorter', apply: () => setLength('SHORT') },
    { label: 'More detailed', apply: () => setLength('DETAILED') },
    { label: 'Strict brand', apply: () => setMode('STRICT_BRAND') },
    { label: 'No generated text', apply: () => setTextPolicy('NO_GENERATED_TEXT') },
  ], []);

  const optimize = async (extra: Partial<{ mode: Mode; length: Length }> = {}) => {
    if (!brief.trim()) return setError('Podaj brief (np. PL lub EN).');
    setBusy('optimize');
    setError(null);
    try {
      const r = await api.imagePromptOptimize({
        brief, subject: subject || undefined, aspectRatio,
        mode: extra.mode ?? mode, length: extra.length ?? length, textPolicy, brandId: brandId || undefined,
        llm,
      });
      setOptimized(r);
      setEditablePrompt(r.prompt);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Błąd optymalizacji prompta.');
    } finally {
      setBusy(null);
    }
  };

  const generate = async () => {
    if (!brandId || !editablePrompt.trim()) return setError('Wybierz brand i wpisz prompt.');
    setBusy('generate');
    setError(null);
    try {
      const asset = await api.mediaGenerate({ brandId, prompt: editablePrompt, aspectRatio });
      setGenerated({ url: asset.url ?? '', provider: asset.provider ?? '' });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Błąd generowania obrazu.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <h2>Image Studio</h2>
      <p className="muted">Brief → VisualBrief → zoptymalizowany prompt EN → generowanie.</p>

      <div className="card">
        <div className="card__header">Brief</div>
        <textarea
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          rows={4}
          placeholder="np. Stwórz grafikę do kampanii… Zamaskowana ekipa ucieka z banku nocą…"
        />
        <div className="grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          <label>Subject <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="EN subject (opcjonalnie)" /></label>
          <label>Aspect ratio
            <select value={aspectRatio} onChange={(e) => setAspectRatio(e.target.value)}>
              {['9:16', '4:5', '1:1', '16:9'].map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </label>
          <label>Mode
            <select value={mode} onChange={(e) => setMode(e.target.value as Mode)}>
              {(['OPTIMIZED', 'DIRECT', 'STRICT_BRAND'] as Mode[]).map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <label>Length
            <select value={length} onChange={(e) => setLength(e.target.value as Length)}>
              {(['SHORT', 'STANDARD', 'DETAILED'] as Length[]).map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <label>Text policy
            <select value={textPolicy} onChange={(e) => setTextPolicy(e.target.value as 'NO_GENERATED_TEXT' | 'TEXT_IN_IMAGE')}>
              <option value="NO_GENERATED_TEXT">NO_GENERATED_TEXT</option>
              <option value="TEXT_IN_IMAGE">TEXT_IN_IMAGE</option>
            </select>
          </label>
          <label>Brand
            <select value={brandId} onChange={(e) => setBrandId(e.target.value)}>
              {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={llm} onChange={(e) => setLlm(e.target.checked)} />
            AI VisualBrief (FAST model)
          </label>
        </div>
        {error && <div className="alert alert--error" style={{ marginTop: 10 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <button className="btn btn--primary" onClick={() => optimize()} disabled={busy === 'optimize'}>
            {busy === 'optimize' ? 'Optymalizacja…' : 'Optimize Prompt'}
          </button>
          {modifiers.map((m) => (
            <button key={m.label} className="btn" onClick={() => { m.apply(); optimize(); }} disabled={busy === 'optimize'}>
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {optimized && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card__header">
            Optimized prompt
            <span className="muted"> · {optimized.mode} · {optimized.aspectRatio} · {optimized.textPolicy} · {optimized.language}</span>
          </div>
          <p className="muted" style={{ fontSize: 12 }}>Brief (oryginalny)</p>
          <pre className="muted" style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{brief}</pre>
          <p className="muted" style={{ fontSize: 12 }}>Visual brief</p>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>
            {optimized.visualBrief?.subject}
            {optimized.visualBrief?.action ? `\n${optimized.visualBrief.action}` : ''}
          </pre>
          <textarea value={editablePrompt} onChange={(e) => setEditablePrompt(e.target.value)} rows={4} style={{ marginTop: 8 }} />
          {optimized.negativePrompt && (
            <p className="muted" style={{ fontSize: 12 }}>Negative: {optimized.negativePrompt}</p>
          )}
          <p className="muted" style={{ fontSize: 12 }}>
            Required capabilities: {optimized.requiredCapabilities.join(', ')}
            {optimized.referenceAssets?.length ? ` · references: ${optimized.referenceAssets.length}` : ''}
          </p>
          <button className="btn btn--primary" style={{ marginTop: 10 }} onClick={generate} disabled={busy === 'generate'}>
            {busy === 'generate' ? 'Generowanie…' : 'Generate image'}
          </button>
        </div>
      )}

      {generated && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card__header">Wygenerowano · {generated.provider}</div>
          <img src={generated.url} alt="generated" style={{ maxWidth: '100%', borderRadius: 8 }} />
        </div>
      )}
    </div>
  );
}